/**
 * Admin utility functions
 * Checks if a user has admin privileges
 */

import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * Check if the current user is an admin
 * Checks the role field in the profiles table
 */
export async function isAdmin(user: User | null): Promise<boolean> {
  if (!user) {
    console.log('[Admin Check] No user provided');
    return false;
  }

  try {
    console.log('[Admin Check] Starting check for user:', user.id, user.email);
    
    // Query profile with role
    const { data, error } = await supabase
      .from('profiles')
      .select('role, user_id')
      .eq('user_id', user.id)
      .maybeSingle(); // Use maybeSingle to handle missing rows gracefully

    console.log('[Admin Check] Query result:', { data, error });

    if (error) {
      console.error('[Admin Check] Database error:', error);
      console.error('[Admin Check] Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      return false;
    }

    if (!data) {
      console.warn('[Admin Check] No profile found for user:', user.id);
      return false;
    }

    // Normalize role value (trim whitespace, handle null)
    const role = data.role?.trim().toLowerCase();
    const isAdminResult = role === 'admin';
    
    console.log('[Admin Check] Final result:', {
      user_id: data.user_id,
      role: data.role,
      normalized_role: role,
      isAdmin: isAdminResult
    });

    return isAdminResult;
  } catch (error) {
    console.error('[Admin Check] Exception:', error);
    return false;
  }
}

/**
 * Check admin status synchronously from user metadata
 * This is a fallback for client-side checks, but server-side checks should always be used
 */
export function isAdminSync(user: User | null): boolean {
  if (!user) return false;
  
  // Check user metadata as fallback
  const role = user.user_metadata?.role || user.app_metadata?.role;
  return role === 'admin';
}
